// @ts-nocheck
import React, { useEffect, useState, useCallback } from "react";
import {
  Typography,
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  Spin,
  message,
  Row,
  Col,
  Avatar,
} from "antd";
import { UserOutlined, SaveOutlined } from "@ant-design/icons";
import { Button } from "../components/ui/Button";
import dayjs from "dayjs";
import { request } from "../api/client";
import { API_URLS } from "../utils/urls";
import { useAppSelector, useAppDispatch } from "../store/hooks";
import { setDisplayName } from "../store/profileSlice";
import { useDisplayName } from "../hooks/useDisplayName";
import PageHeader from "../components/PageHeader";

const { Title, Text } = Typography;

export default function Profile() {
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const displayName = useDisplayName();
  const [form] = Form.useForm();

  const [fieldGroups, setFieldGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [fieldsRes, profileRes] = await Promise.all([
        request(API_URLS.profileFields, { auth: true }),
        request(API_URLS.profile, { auth: true }),
      ]);

      const groups = Array.isArray(fieldsRes?.groups) ? fieldsRes.groups : [];
      const savedFields = profileRes?.fields ?? {};

      setFieldGroups(groups);

      // Populate form with saved values, converting date strings to dayjs for DatePicker
      const formValues = {};
      groups.forEach((g) => {
        g.fields.forEach((f) => {
          const v = savedFields[f.key] ?? undefined;
          if (f.type === "date" && v) {
            formValues[f.key] = dayjs(v).isValid() ? dayjs(v) : undefined;
          } else {
            formValues[f.key] = v;
          }
        });
      });
      formValues.displayName =
        typeof savedFields.displayName === "string" ? savedFields.displayName : "";
      form.setFieldsValue(formValues);
    } catch (e) {
      message.error(e?.message || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (values) => {
    try {
      setSaving(true);
      const fields: Record<string, string> = {};
      Object.entries(values).forEach(([fieldKey, value]) => {
        if (value === undefined || value === null || value === "") {
          fields[fieldKey] = "";
          return;
        }
        // Convert dayjs back to ISO string for date fields
        if (dayjs.isDayjs(value)) {
          fields[fieldKey] = value.format("YYYY-MM-DD");
        } else {
          fields[fieldKey] = String(value).trim();
        }
      });
      // Always send the display name (even when empty) so the user can clear it
      // and fall back to their Auth0 name.
      fields.displayName = String(values.displayName ?? "").trim();
      await request(API_URLS.profile, {
        method: "PUT",
        auth: true,
        body: { fields },
      });
      dispatch(setDisplayName(fields.displayName));
      message.success("Profile saved");
    } catch (e) {
      message.error(e?.message || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field) => {
    if (field.type === "select") {
      return (
        <Select
          placeholder={`Select ${field.label}`}
          allowClear
          options={(field.options || []).map((o) => ({ label: o, value: o }))}
        />
      );
    }
    if (field.type === "date") {
      return (
        <DatePicker
          className="w-full"
          placeholder={field.placeholder || "YYYY-MM-DD"}
          format="YYYY-MM-DD"
        />
      );
    }
    return <Input placeholder={field.placeholder || field.label} />;
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  const GROUP_HINTS: Record<string, string> = {
    Personal: "Helps us tailor your AI-generated plans to your life stage and circumstances.",
    Location: "Used to suggest locally relevant goals, resources, and cost estimates.",
    Background: "Helps personalise recommendations based on your education and language preferences.",
  };

  return (
    <div className="space-y-6 overflow-x-hidden">
      <PageHeader
        title={displayName || "Profile"}
        subtitle={user?.email || "Manage your personal information"}
      />

      {/* Dynamic field groups */}
      <Form form={form} layout="vertical" onFinish={handleSave}>
        <Card title="Display Name" className="mb-4">
          <p className="mb-4 mt-0 text-[12px]" style={{ color: "#6B7280" }}>
            This is the name shown across DreamsUp. It's for display only — billing
            and your account always use your verified name. Leave it blank to use
            {user?.name ? ` "${user.name}"` : " your account name"}.
          </p>
          <Form.Item name="displayName" label="Display Name" className="mb-0">
            <Input placeholder={user?.name || "Your name"} maxLength={60} allowClear />
          </Form.Item>
        </Card>

        {fieldGroups.map((group) => (
          <Card
            key={group.group}
            title={group.group}
            className="mb-4"
          >
            {GROUP_HINTS[group.group] && (
              <p className="mb-4 mt-0 text-[12px]" style={{ color: "#6B7280" }}>
                {GROUP_HINTS[group.group]}
              </p>
            )}
            <Row gutter={{ xs: 0, sm: 16 } as any}>
              {group.fields.map((field) => (
                <Col key={field.key} xs={24} sm={12}>
                  <Form.Item
                    name={field.key}
                    label={field.label}
                    role="group"
                    aria-label={field.label}
                    rules={
                      field.required
                        ? [{ required: true, message: `${field.label} is required` }]
                        : []
                    }
                  >
                    {renderField(field)}
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </Card>
        ))}

        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={saving}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            {!saving && <SaveOutlined />}
            Save Profile
          </Button>
        </div>
      </Form>
    </div>
  );
}
